
import { useState } from 'react';
import { supabase } from '../../../lib/supabaseClient';

interface Carta {
  id: string;
  data_entrega: string;
  plano?: string;
  status?: string;
  lembretes_ativados?: boolean;
  prazo_anos?: number;
  prazo?: string;
}

interface LembretesPanelProps {
  cartas: Carta[];
  onRefresh: () => void;
}

const LembretesPanel = ({ cartas, onRefresh }: LembretesPanelProps) => {
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState(false);

  const cartasAtivas = cartas.filter((c) => {
    const entrega = new Date(c.data_entrega);
    const agora = new Date();
    return entrega > agora;
  });

  if (cartasAtivas.length === 0) return null;

  const toggleLembrete = async (cartaId: string, ativado: boolean) => {
    setTogglingId(cartaId);
    try {
      await supabase
        .from('cartas')
        .update({ lembretes_ativados: !ativado })
        .eq('id', cartaId);
      onRefresh();
    } catch (err) {
      console.error('Erro ao atualizar lembrete:', err);
    } finally {
      setTogglingId(null);
    }
  };

  const calcularProximoLembrete = (dataEntrega: string) => {
    const entrega = new Date(dataEntrega);
    const agora = new Date();
    const diffMs = entrega.getTime() - agora.getTime();
    const diasRestantes = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    const marcos = [30, 7, 3, 1];
    for (const marco of marcos) {
      if (diasRestantes > marco) {
        const dataLembrete = new Date(entrega);
        dataLembrete.setDate(dataLembrete.getDate() - marco);
        return {
          dias: marco,
          data: dataLembrete,
          label: marco === 30 ? '30 dias antes' : marco === 7 ? '7 dias antes' : marco === 3 ? '3 dias antes' : '1 dia antes',
        };
      }
    }

    if (diasRestantes === 1) {
      return { dias: 1, data: agora, label: 'Amanh\u00e3!' };
    }

    return null;
  };

  const formatarData = (data: Date) => {
    return data.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="mb-12">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gold-500/10 border border-gold-500/30 flex items-center justify-center">
            <i className="ri-notification-3-line text-xl text-gold-500"></i>
          </div>
          <div>
            <h2 className="font-['Playfair_Display'] text-xl text-white">
              Lembretes
            </h2>
            <p className="font-['Montserrat'] text-xs text-white/40 tracking-wide">
              Receba avisos antes da entrega
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowInfo(!showInfo)}
          className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors cursor-pointer"
        >
          <i className={`ri-${showInfo ? 'close' : 'question'}-line text-white/40 text-sm`}></i>
        </button>
      </div>

      {showInfo && (
        <div className="mb-6 bg-gold-500/5 border border-gold-500/20 rounded-xl p-5 animate-fadeIn">
          <div className="flex items-start gap-3">
            <i className="ri-lightbulb-line text-gold-400 mt-0.5"></i>
            <div>
              <p className="font-['Montserrat'] text-sm text-white/70 leading-relaxed mb-3">
                Voc\u00ea receber\u00e1 lembretes por email nos seguintes momentos:
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: 'ri-calendar-line', label: '30 dias antes', desc: 'Primeiro aviso' },
                  { icon: 'ri-timer-line', label: '7 dias antes', desc: 'Uma semana!' },
                  { icon: 'ri-alarm-line', label: '3 dias antes', desc: 'Contagem regressiva' },
                  { icon: 'ri-star-line', label: '1 dia antes', desc: '\u00c9 amanh\u00e3!' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-2 bg-white/[0.03] rounded-lg p-3">
                    <div className="w-7 h-7 rounded-md bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-gold-400 text-xs`}></i>
                    </div>
                    <div>
                      <p className="font-['Montserrat'] text-xs text-white/80 font-medium">{item.label}</p>
                      <p className="font-['Montserrat'] text-xs text-white/30">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {cartasAtivas.map((carta) => {
          const lembretesAtivados = carta.lembretes_ativados !== false;
          const proximoLembrete = calcularProximoLembrete(carta.data_entrega);
          const isToggling = togglingId === carta.id;
          const isPremium = carta.plano === 'premium';
          const prazoLabel = `${carta.prazo_anos || carta.prazo} ${(carta.prazo_anos || parseInt(carta.prazo || '0')) === 1 ? 'ano' : 'anos'}`;

          return (
            <div
              key={carta.id}
              className={`bg-luxury-dark/60 backdrop-blur-xl border rounded-xl p-5 transition-all duration-300 ${
                lembretesAtivados ? 'border-gold-500/20 hover:border-gold-500/40' : 'border-white/5 opacity-60'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    lembretesAtivados ? 'bg-gold-500/10' : 'bg-white/5'
                  }`}>
                    <i className={`${lembretesAtivados ? 'ri-notification-3-fill text-gold-400' : 'ri-notification-off-line text-white/30'} text-lg`}></i>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-['Montserrat'] ${
                        isPremium ? 'bg-gold-500/10 text-gold-400 border border-gold-500/20' : 'bg-white/5 text-white/40 border border-white/10'
                      }`}>
                        <i className={`text-xs ${isPremium ? 'ri-vip-crown-2-fill' : 'ri-gift-line'}`}></i>
                        {isPremium ? 'Premium' : 'Gratuito'}
                      </span>
                      <span className="font-['Montserrat'] text-xs text-white/30">
                        {prazoLabel}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {lembretesAtivados && proximoLembrete ? (
                        <p className="font-['Montserrat'] text-xs text-white/60 truncate">
                          <i className="ri-time-line mr-1 text-gold-400"></i>
                          Pr\u00f3ximo: <span className="text-gold-400 font-medium">{proximoLembrete.label}</span>
                          <span className="text-white/30 ml-1">({formatarData(proximoLembrete.data)})</span>
                        </p>
                      ) : lembretesAtivados ? (
                        <p className="font-['Montserrat'] text-xs text-green-400">
                          <i className="ri-check-line mr-1"></i>
                          Todos os lembretes enviados
                        </p>
                      ) : (
                        <p className="font-['Montserrat'] text-xs text-white/30">
                          Lembretes desativados
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => toggleLembrete(carta.id, lembretesAtivados)}
                  disabled={isToggling}
                  className={`relative w-12 h-7 rounded-full transition-all duration-300 flex-shrink-0 cursor-pointer ${
                    lembretesAtivados ? 'bg-gold-500' : 'bg-white/10'
                  } ${isToggling ? 'opacity-50' : ''}`}
                >
                  <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-all duration-300 ${
                    lembretesAtivados ? 'left-6' : 'left-1'
                  }`}>
                    {isToggling && (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="w-3 h-3 border-2 border-gold-500 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LembretesPanel;
